import React, { useEffect, useState } from "react";
import { db } from "../firebase";
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp,updateDoc } from "firebase/firestore";
import './AdminDashboard.css';
import { useNavigate } from 'react-router-dom';

const AdminAchievementPage = () => {
    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState("");
    const [achievements, setAchievements] = useState([]);
    const [globalQuests, setGlobalQuests] = useState([]);
    const navigate = useNavigate();
    const [editedAchievements, setEditedAchievements] = useState({});

    const [newAchievement, setNewAchievement] = useState({
        name: "",
        description: "",
        icon: "",
        special: false
    });

    // Bulk achievement states
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [showBulkMode, setShowBulkMode] = useState(false);
    const [bulkAchievement, setBulkAchievement] = useState({
        name: "",
        description: "",
        icon: "",
        special: false
    });

    const handleEditChange = (id, field, value) => {
        setEditedAchievements(prev => ({
            ...prev,
            [id]: { ...prev[id], [field]: value }
        }));
    };

    const handleSaveEdit = async (id) => {
        const updated = editedAchievements[id];
        if (!updated) return;

        const achievementRef = doc(db, 'users', selectedUser, 'achievements', id);
        await updateDoc(achievementRef, updated);

        setEditedAchievements(prev => {
            const copy = { ...prev };
            delete copy[id];
            return copy;
        });

        loadAchievements(selectedUser);
    };

    // Load users
    const loadUsers = async () => {
        const snapshot = await getDocs(collection(db, 'users'));
        const userList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        
        // Sort users by YP (descending) for ranking - highest YP first
        const sortedUsers = userList.sort((a, b) => {
            const ypA = Number(a.YP) || 0;
            const ypB = Number(b.YP) || 0;
            return ypB - ypA; // Descending order (highest first)
        });
        
        console.log('Sorted users by YP:', sortedUsers.map(u => ({ pseudo: u.pseudo, YP: u.YP })));
        
        setUsers(sortedUsers);
    };

    const loadGlobalQuests = async () => {
        const snapshot = await getDocs(collection(db, 'quests'));
        const questList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setGlobalQuests(questList);
    };


    useEffect(() => {
        loadUsers();
        loadGlobalQuests();
    }, []);

    // Load achievements for selected user
    const loadAchievements = async (userId) => {
        if (!userId) {
            setAchievements([]);
            return;
        }
        const colRef = collection(db, 'users', userId, 'achievements');
        const snapshot = await getDocs(colRef);
        const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setAchievements(docs);
    };

    useEffect(() => {
        if (selectedUser) {
            loadAchievements(selectedUser);
        } else {
            setAchievements([]);
        }
    }, [selectedUser]);

    // Add new achievement
    const handleAddAchievement = async () => {
        const { name, description, icon, special } = newAchievement;
        if (!name || !description || !icon || !selectedUser) {
            alert("Please fill all fields and select a user.");
            return;
        }
        await addDoc(collection(db, 'users', selectedUser, 'achievements'), {
            name,
            description,
            icon,
            special,
            dateEarned: serverTimestamp()
        });
        setNewAchievement({ name: "", description: "", icon: "" });
        loadAchievements(selectedUser);
    };

    // Delete achievement
    const handleDeleteAchievement = async (id) => {
        if (!window.confirm("Delete this achievement?")) return;
        await deleteDoc(doc(db, 'users', selectedUser, 'achievements', id));
        loadAchievements(selectedUser);
    };

    // Bulk achievement handlers
    const handleToggleUser = (userId) => {
        setSelectedUsers(prev => 
            prev.includes(userId) 
                ? prev.filter(id => id !== userId)
                : [...prev, userId]
        );
    };

    const handleSelectAllUsers = () => {
        if (selectedUsers.length === users.length) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(u => u.id));
        }
    };

    const handleBulkAddAchievement = async () => {
        const { name, description, icon, special } = bulkAchievement;
        if (!name || !description || !icon) {
            alert("Please fill all achievement fields.");
            return;
        }
        if (selectedUsers.length === 0) {
            alert("Please select at least one user.");
            return;
        }

        if (!window.confirm(`Add "${name}" achievement to ${selectedUsers.length} user(s)?`)) {
            return;
        }

        try {
            const promises = selectedUsers.map(userId =>
                addDoc(collection(db, 'users', userId, 'achievements'), {
                    name,
                    description,
                    icon,
                    special,
                    dateEarned: serverTimestamp()
                })
            );
            await Promise.all(promises);
            
            alert(`Achievement added to ${selectedUsers.length} user(s) successfully!`);
            setBulkAchievement({ name: "", description: "", icon: "", special: false });
            setSelectedUsers([]);
            
            // Reload achievements if current user was selected
            if (selectedUser && selectedUsers.includes(selectedUser)) {
                loadAchievements(selectedUser);
            }
        } catch (error) {
            console.error("Error adding bulk achievement:", error);
            alert("Error adding achievement to users.");
        }
    };

    return (
        <div className="admin-container">
            <h2>🏅 Achievement Management</h2>

            {/* Mode Toggle */}
            <div className="admin-section">
                <button 
                    onClick={() => setShowBulkMode(!showBulkMode)}
                    style={{
                        padding: '10px 20px',
                        fontSize: '16px',
                        backgroundColor: showBulkMode ? '#4CAF50' : '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                    }}
                >
                    {showBulkMode ? '👤 Switch to Single User Mode' : '👥 Switch to Bulk Mode'}
                </button>
            </div>

            {showBulkMode ? (
                /* BULK MODE */
                <>
                    <div className="admin-section">
                        <h3>Bulk Achievement Assignment</h3>
                        <p style={{ color: '#888', marginBottom: '10px' }}>
                            Select multiple users to receive the same achievement
                        </p>
                        
                        {/* User Selection */}
                        <div style={{ marginBottom: '20px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4>Select Users ({selectedUsers.length} selected)</h4>
                                <button 
                                    onClick={handleSelectAllUsers}
                                    style={{
                                        padding: '5px 15px',
                                        backgroundColor: '#666',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '3px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    {selectedUsers.length === users.length ? 'Deselect All' : 'Select All'}
                                </button>
                            </div>
                            <div style={{
                                maxHeight: '600px',
                                overflowY: 'auto',
                                border: '1px solid #444',
                                borderRadius: '5px',
                                padding: '10px',
                                backgroundColor: '#1a1a1a'
                            }}>
                                {users.map((u, index) => {
                                    // Calculate rank based on position
                                    const rank = index + 1;
                                    
                                    // Determine division based on rank
                                    let divisionColor = '#8B0000'; // Red Slifer
                                    let divisionLabel = 'Red Slifer';
                                    
                                    if (rank >= 1 && rank <= 4) {
                                        divisionColor = '#1E90FF'; // Blue Obelisk
                                        divisionLabel = 'Blue Obelisk';
                                    } else if (rank >= 5 && rank <= 12) {
                                        divisionColor = '#FFD700'; // Yellow Ra
                                        divisionLabel = 'Yellow Ra';
                                    }
                                    
                                    return (
                                    <div 
                                        key={u.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            padding: '8px',
                                            marginBottom: '5px',
                                            backgroundColor: selectedUsers.includes(u.id) ? '#2a4a2a' : '#2a2a2a',
                                            borderRadius: '3px',
                                            cursor: 'pointer',
                                            border: selectedUsers.includes(u.id) ? '2px solid #4CAF50' : `2px solid ${divisionColor}`,
                                            borderLeft: `6px solid ${divisionColor}`
                                        }}
                                        onClick={() => handleToggleUser(u.id)}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(u.id)}
                                            onChange={() => handleToggleUser(u.id)}
                                            style={{ marginRight: '10px', cursor: 'pointer' }}
                                        />
                                        <span style={{ 
                                            marginRight: '10px', 
                                            fontWeight: 'bold',
                                            color: divisionColor,
                                            minWidth: '35px'
                                        }}>
                                            #{rank}
                                        </span>
                                        <span style={{ flex: 1 }}>{u.pseudo || u.id}</span>
                                        <span style={{ 
                                            fontSize: '11px', 
                                            color: divisionColor,
                                            marginLeft: '10px',
                                            fontWeight: 'bold'
                                        }}>
                                            {divisionLabel} ({u.YP || 0} YP)
                                        </span>
                                    </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Achievement Form */}
                        <div className="form-grid">
                            <div>
                                <label>Quest Import</label>
                                <select
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const quest = globalQuests.find(q => q.id === selectedId);
                                        if (quest) {
                                            setBulkAchievement({
                                                name: quest.title,
                                                description: quest.description,
                                                icon: quest.imageUrl,
                                                special: false
                                            });
                                        }
                                    }}
                                >
                                    <option value="">-- Select a Quest --</option>
                                    {globalQuests.map(q => (
                                        <option key={q.id} value={q.id}>
                                            {q.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label>Name</label>
                                <input
                                    value={bulkAchievement.name}
                                    onChange={(e) => setBulkAchievement({ ...bulkAchievement, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label>Description</label>
                                <input
                                    value={bulkAchievement.description}
                                    onChange={(e) => setBulkAchievement({ ...bulkAchievement, description: e.target.value })}
                                />
                            </div>
                            <div>
                                <label>Icon URL</label>
                                <input
                                    value={bulkAchievement.icon}
                                    onChange={(e) => setBulkAchievement({ ...bulkAchievement, icon: e.target.value })}
                                />
                            </div>
                            <div>
                                <label>Special</label>
                                <input
                                    type="checkbox"
                                    checked={bulkAchievement.special}
                                    onChange={(e) =>
                                        setBulkAchievement({ ...bulkAchievement, special: e.target.checked })
                                    }
                                />
                            </div>

                            <button 
                                onClick={handleBulkAddAchievement}
                                style={{
                                    backgroundColor: '#4CAF50',
                                    padding: '10px 20px',
                                    fontSize: '16px'
                                }}
                            >
                                Add Achievement to {selectedUsers.length} User(s)
                            </button>
                        </div>
                    </div>
                </>
            ) : (
                /* SINGLE USER MODE */
                <>
                    <div className="admin-section">
                        <h3>Select User</h3>
                        <select
                            value={selectedUser}
                            onChange={(e) => setSelectedUser(e.target.value)}
                        >
                            <option value="">-- Select a User --</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.pseudo || u.id}
                                </option>
                            ))}
                        </select>
                    </div>

            {selectedUser && (
                <>
                    <div className="admin-section">
                        <h3>Add New Achievement</h3>
                        <div className="form-grid">
                            <div>
                                <label>Quest Import</label>
                                <select
                                    onChange={(e) => {
                                        const selectedId = e.target.value;
                                        const quest = globalQuests.find(q => q.id === selectedId);
                                        if (quest) {
                                            setNewAchievement({
                                                name: quest.title,
                                                description: quest.description,
                                                icon: quest.imageUrl,
                                                special: false
                                            });

                                        }
                                    }}
                                >
                                    <option value="">-- Select a Quest --</option>
                                    {globalQuests.map(q => (
                                        <option key={q.id} value={q.id}>
                                            {q.title}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label>Name</label>
                                <input
                                    value={newAchievement.name}
                                    onChange={(e) => setNewAchievement({ ...newAchievement, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label>Description</label>
                                <input
                                    value={newAchievement.description}
                                    onChange={(e) => setNewAchievement({ ...newAchievement, description: e.target.value })}
                                />
                            </div>
                            <div>
                                <label>Icon URL</label>
                                <input
                                    value={newAchievement.icon}
                                    onChange={(e) => setNewAchievement({ ...newAchievement, icon: e.target.value })}
                                />
                            </div>
                            <div>
                                <label>Special</label>
                                <input
                                    type="checkbox"
                                    checked={newAchievement.special}
                                    onChange={(e) =>
                                        setNewAchievement({ ...newAchievement, special: e.target.checked })
                                    }
                                />
                            </div>

                            <button onClick={handleAddAchievement}>Add Achievement</button>
                        </div>
                    </div>


                    <div className="admin-section">
                        <h3>Existing Achievements</h3>
                        {achievements.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        <th>Icon</th>
                                        <th>Name</th>
                                        <th>Description</th>
                                        <th>Date Earned</th>
                                        <th>Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {achievements.map(a => {
                                        const isEditing = editedAchievements[a.id] !== undefined;
                                        const edited = editedAchievements[a.id] || {};

                                        return (
                                            <tr key={a.id}>
                                                <td>
                                                    <img
                                                        src={edited.icon || a.icon}
                                                        alt={a.name}
                                                        style={{
                                                            width: "64px",
                                                            height: "64px",
                                                            objectFit: "cover",
                                                            borderRadius: "8px"
                                                        }}
                                                    />
                                                    {isEditing && (
                                                        <input
                                                            type="text"
                                                            value={edited.icon || ""}
                                                            onChange={(e) => handleEditChange(a.id, "icon", e.target.value)}
                                                            placeholder="Icon URL"
                                                        />
                                                    )}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <input
                                                            value={edited.name || ""}
                                                            onChange={(e) => handleEditChange(a.id, "name", e.target.value)}
                                                        />
                                                    ) : (
                                                        a.name
                                                    )}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <input
                                                            value={edited.description || ""}
                                                            onChange={(e) => handleEditChange(a.id, "description", e.target.value)}
                                                        />
                                                    ) : (
                                                        a.description
                                                    )}
                                                </td>
                                                <td>
                                                    {a.dateEarned?.seconds
                                                        ? new Date(a.dateEarned.seconds * 1000).toLocaleDateString()
                                                        : "N/A"}
                                                </td>
                                                <td>
                                                    {isEditing ? (
                                                        <button onClick={() => handleSaveEdit(a.id)}>💾</button>
                                                    ) : (
                                                        <>
                                                            <button onClick={() => setEditedAchievements({ [a.id]: { name: a.name, description: a.description, icon: a.icon } })}>✏️</button>
                                                            <button onClick={() => handleDeleteAchievement(a.id)}>🗑️</button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}

                                </tbody>
                            </table>
                        ) : (
                            <p>No achievements for this user.</p>
                        )}
                    </div>

                </>
            )}
                </>
            )}
            
            <div className="tournament-actions" style={{ marginTop: '20px' }}>
                <button onClick={() => navigate('/admin-panelx-952504')}>⬅️ Back to Dashboard</button>
            </div>
        </div>
    );
};

export default AdminAchievementPage;
